import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import { useDatabaseContent } from '@databases/database-state';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { selectCardLayoutDayEntries } from '@databases/redux/calendar-layout/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { appStoreUtils } from '@databases/redux/store';
import { DatabaseFieldType, DatabaseValue } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { createEntry } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

import AddEntryButton from './AddEntryButton';
import { EntryCardProps } from './Calendar';
import EntryCard from './EntryCard';

const AddNewEntryStyles = xcss({
  position: 'absolute',
  top: 'space.100',
  right: 'space.100',
  width: '32px',
});

const GridStyles = xcss({
  position: 'relative',
  borderColor: 'color.border',
  borderWidth: 'border.width',
  borderStyle: 'solid',
});

const GridWeekendStyles = xcss({
  backgroundColor: 'color.background.neutral',
});

const gridDragOverStyles = xcss({
  backgroundColor: 'color.background.selected',
});

const GridCurrentDayStyles = xcss({
  borderWidth: 'border.width.0',
  borderTopWidth: 'border.width.indicator',
  borderColor: 'color.border.selected',
  borderStyle: 'solid',
  backgroundColor: 'color.background.selected',
});

const DayCard = ({
  canEditContent,
  EntryCardValueComponent,
  day,
  isCurrentDay,
  isFirstRow,
  isFirstColumn,
  isLastColumn,
}: {
  canEditContent: boolean;
  EntryCardValueComponent: React.ComponentType<EntryCardProps>;
  day: dayjs.Dayjs;
  isCurrentDay: boolean;
  isFirstRow: boolean;
  isFirstColumn: boolean;
  isLastColumn: boolean;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const { contentId } = useDatabaseContent() as { contentId: string };
  const { view } = useView();
  const { showPanel } = usePanel();
  const dispatch = appStoreUtils.useDispatch();

  const database = defaultStoreUtils.useSelector((state) => {
    return selectDatabaseByContentIdRequired(state, { contentId });
  });

  const entryIds = defaultStoreUtils.useEqualSelector((state) => {
    return selectCardLayoutDayEntries(state, {
      contentId,
      viewSettings: view.settings,
      date: day.format('YYYY-MM-DD'),
    });
  });

  const onAddNewEntry = useCallback(() => {
    const customValues: Record<string, DatabaseValue> = {
      [view.settings.layout.dateFieldId!]: {
        type: DatabaseFieldType.DATE,
        value: day.format('YYYY-MM-DD'),
      },
    };

    const entry = createEntry(database, view.settings);
    if (customValues) {
      Object.assign(entry.values, customValues);
    }

    dispatch(
      addChangeToDraft(contentId, 'main', {
        type: DatabaseChangeType.ADD_ENTRY,
        entry,
      })
    );

    showPanel('EntryViewPanel', {
      entryId: entry.id,
      contentId,
    });
  }, [contentId, database, day, dispatch, showPanel, view.settings]);

  // set isHovered when components is hovered
  useEffect(() => {
    const el = ref.current;
    invariant(el);

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    invariant(el);

    return dropTargetForElements({
      element: el,
      getData: () => ({ day }),
      canDrop: () => canEditContent,
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    });
  }, [canEditContent, day]);

  return (
    <Box
      padding="space.100"
      xcss={[
        GridStyles,
        (isFirstColumn || isLastColumn) && GridWeekendStyles,
        isCurrentDay && GridCurrentDayStyles,
        isDraggedOver && gridDragOverStyles,
      ]}
      ref={ref}
    >
      <Box xcss={AddNewEntryStyles}>
        {canEditContent && isHovered ? <AddEntryButton onClick={onAddNewEntry} /> : null}
      </Box>
      <Stack space="space.150">
        <Stack space="space.050">
          <Inline alignInline="center" space="space.100">
            {isFirstRow ? <Text weight="bold">{day.format('dd')}</Text> : null}
          </Inline>
          <Inline alignInline="center" space="space.100">
            <Text weight="bold">{day.format('D')}</Text>
          </Inline>
        </Stack>
        <Stack space="space.100">
          {entryIds.map((entryId) => (
            <EntryCard
              entryId={entryId}
              key={entryId}
              EntryCardValueComponent={EntryCardValueComponent}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default DayCard;
